import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  CheckCircle,
  MessageSquare,
  FileText,
  AlertTriangle
} from 'lucide-react';

// ⚠️ Déclaration l'URL API 

//const API_URL = 'http://localhost:5000/api/contact'; 

// Exemple d'utilisation dans ton code React :
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Appel POST:
const API_URL = `${API_BASE_URL}/contact`;

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formType, setFormType] = useState('contact');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  

 const onSubmit = async (data) => {
  const finalData = { ...data, formType }; 
  setIsSubmitting(true);

  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(finalData),
    });

    const result = await response.json();
    
    // 🎯 LOG DÉTAILLÉ DE LA RÉPONSE
    console.log('📤 Données envoyées:', finalData);
    console.log('📥 Réponse reçue:', result);
    console.log('📊 Status:', response.status);

    if (!response.ok) {
      // 🎯 LOG DES ERREURS DE VALIDATION
      if (result.errors && Array.isArray(result.errors)) {
        console.error('❌ Erreurs de validation:', result.errors);
        const errorMessages = result.errors.map(err => err.msg).join(', ');
        throw new Error(errorMessages);
      }
      throw new Error(result.message || 'Échec de la soumission du formulaire');
    }

    toast.success('Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.');
    reset();
    
  } catch (error) {
    console.error('❌ Erreur de soumission du formulaire:', error);
    toast.error(error.message || 'Erreur lors de l\'envoi du message. Veuillez réessayer.');
  } finally {
    setIsSubmitting(false);
  }
};

  const contactTypes = [
    {
      id: 'contact',
      title: 'Contact Général',
      description: 'Questions générales et informations',
      icon: MessageSquare,
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'quote',
      title: 'Demande de Devis',
      description: 'Devis personnalisé pour nos services',
      icon: FileText,
      color: 'from-green-500 to-green-600'
    },
    {
      id: 'support',
      title: 'Support Technique',
      description: 'Assistance technique et résolution de problèmes',
      icon: AlertTriangle,
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Adresse',
      content: 'liberté 6 extension villa 27\nDakar\nSénégal',
      color: 'text-blue-600'
    },
    {
      icon: Phone,
      title: 'Téléphone',
      content: '+221 77 557 77 48\n+221 33 802 84 84',
      color: 'text-green-600'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'lst@lstoil.com\nlstlamine@gmail.com',
      color: 'text-purple-600'
    },
    {
      icon: Clock,
      title: 'Horaires',
      content: 'Lun-Ven: 8h-17h\n',
      color: 'text-orange-600'
    }
  ];

  

  return (
    <>
     <Helmet>
        <title>Contact - LST OIL SA | Logistique Pétrolière & Transport Dakar</title>
        <meta
          name="description"
          content="Contactez LST OIL SA pour logistique pétrolière, transport d'hydrocarbures et produits alimentaires. Experts en solutions logistiques au Sénégal et en Afrique."
        />
        <meta
          name="keywords"
          content="LST OIL SA contact, logistique Sénégal, transport hydrocarbures Dakar, logistique alimentaire, société transport Afrique, devis logistique, contact LST OIL"
        />
        <link rel="canonical" href="https://www.lstoilsa.com/contact" />

        <meta property="og:title" content="Contact - LST OIL SA | Logistique & Transport Expert" />
        <meta
          property="og:description"
          content="Expert en logistique pétrolière, transport et solutions supply chain. Contactez LST OIL SA pour des services sur mesure au Sénégal."
        />
        <meta property="og:url" content="https://www.lstoilsa.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="LST OIL SA" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact - LST OIL SA" />
        <meta name="twitter:description" content="Contactez LST OIL SA pour logistique pétrolière, transport et services au Sénégal." />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact LST OIL SA",
            "description": "Page de contact de LST OIL SA - Expert en logistique pétrolière et transport",
            "url": "https://www.lstoilsa.com/contact",
            "mainEntity": {
              "@type": "Organization",
              "name": "LST OIL SA",
              "description": "Entreprise leader en logistique pétrolière, transport d'hydrocarbures et solutions logistiques intégrées",
              "url": "https://www.lstoilsa.com",
              "telephone": "+221775577748",
              "email": "lst@lstoil.com",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Liberté 6 Extension Villa 27",
                "addressLocality": "Dakar",
                "addressCountry": "SN"
              },
              "openingHours": "Mo-Fr 08:00-17:00, Sa 08:00-12:00",
              "areaServed": "Africa",
              "knowsAbout": [
                "Logistique pétrolière",
                "Transport d'hydrocarbures",
                "Logistique alimentaire",
                "Supply chain management",
                "Transport multimodal"
              ]
            }
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-16 md:py-20 bg-gradient-to-br from-[#6BB8B1] via-[#4B9D96] to-[#3A7C76] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-8 md:mb-6 font-heading">
              Contactez-nous
            </h1>
            <p className="text-lg md:text-2xl text-white max-w-4xl mx-auto leading-relaxed">
              Notre équipe est à votre disposition pour répondre à toutes vos questions
              et vous accompagner dans vos projets
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Types */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-10 md:mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-heading">
              Comment pouvons-nous vous aider ?
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Choisissez le type de contact qui correspond le mieux à votre besoin
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {contactTypes.map((type) => (
              <motion.button
                key={type.id}
                onClick={() => setFormType(type.id)}
                className={`p-6 rounded-xl border-2 transition-all duration-300 text-left ${formType === type.id
                  ? `border-[#4B9D96] bg-blue-20 shadow-lg`
                  : 'border-gray-200 hover:border-[#3A7C76] hover:bg-gray-50'
                  }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${type.color} flex items-center justify-center mb-4`}>
                  <type.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {type.title}
                </h3>
                <p className="text-gray-600">
                  {type.description}
                </p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white p-6 md:p-8 rounded-xl shadow-lg"
            >
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
                Envoyez-nous votre message
              </h3>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      {...register('prenom', { required: 'Le prénom est requis' })}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.prenom ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Votre prénom"
                    />
                    {errors.prenom && (
                      <p className="text-red-500 text-sm mt-1">{errors.prenom.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      {...register('nom', { required: 'Le nom est requis' })}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.nom ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Votre nom"
                    />
                    {errors.nom && (
                      <p className="text-red-500 text-sm mt-1">{errors.nom.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      {...register('email', {
                        required: 'L\'email est requis',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Adresse email invalide'
                        }
                      })}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="votre@email.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      {...register('telephone')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+242 06 123 45 67"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Entreprise
                  </label>
                  <input
                    type="text"
                    {...register('entreprise')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nom de votre entreprise"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sujet *
                  </label>
                  <input
                    type="text"
                    {...register('sujet', { required: 'Le sujet est requis' })}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.subject ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Sujet de votre message"
                  />
                  {errors.sujet && (
                    <p className="text-red-500 text-sm mt-1">{errors.sujet.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    {...register('messages', {
                      required: 'Le message est requis',
                      minLength: {
                        value: 10,
                        message: 'Le message doit contenir au moins 10 caractères'
                      }
                    })}
                    rows={5}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.message ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Décrivez votre besoin ou votre question..."
                  />
                  {errors.messages && (
                    <p className="text-red-500 text-sm mt-1">{errors.messages.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#4b9d96] hover:bg-[#4b9d96] disabled:bg-[#3a7c76] text-white font-bold py-4 px-8 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Envoyer le Message
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8">
                Informations de Contact
              </h3>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-4"
                  >
                    <div className={`w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0`}>
                      <info.icon className={`w-6 h-6 ${info.color}`} />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">
                        {info.title}
                      </h4>
                      <p className="text-gray-600 whitespace-pre-line">
                        {info.content}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Additional Info */}
              <div className="mt-12 p-6 bg-[#6bb8b1] rounded-xl">
                <h4 className="text-lg font-semibold text-white mb-3">
                  Besoin d'une réponse rapide ?
                </h4>
                <p className="text-white mb-4">
                  Pour les demandes urgentes, n'hésitez pas à nous appeler directement.
                  Notre équipe est disponible pour vous répondre rapidement.
                </p>
                <a 
                  
                  href="tel:+221775577748"
                  className="inline-flex items-center gap-2 bg-[#cd3127] text-white px-4 py-2 rounded-lg hover:bg-[#cd3127] transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  Appeler maintenant
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-10 md:mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-heading">
              Notre Localisation
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Situés au cœur de la zone industrielle, nous sommes facilement accessibles
            </p>
          </motion.div>

          <div className="bg-gray-200 h-96 rounded-xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3858.8!2d-17.4721125!3d14.736297!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xec10da4b43d3723%3A0x1cad85f8fe9e9399!2sLST%20OIL%20SA!5e0!3m2!1sfr!2ssn!4v1695000000000!5m2!1sfr!2ssn"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Notre localisation - LST OIL SA, Liberté 6 Extension Pentola Villa n°27, Dakar"
            ></iframe>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-10 md:mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-heading">
              Questions Fréquentes
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Trouvez rapidement des réponses à vos questions
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                question: "Quels sont vos délais de réponse ?",
                answer: "Nous nous engageons à répondre à toutes les demandes dans un délai maximum de 24h en semaine."
              },
              {
                question: "Proposez-vous des services d'urgence ?",
                answer: "Oui, nous disposons d'une équipe d'urgence disponible 24h/24 pour les situations critiques."
              },
              {
                question: "Quels sont vos tarifs ?",
                answer: "Nos tarifs varient selon vos besoins spécifiques. Contactez-nous pour un devis personnalisé."
              },
              {
                question: "Intervenez-vous à l'international ?",
                answer: "Absolument ! Nous opérons dans plus de 50 pays et proposons des services logistiques internationaux."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow-lg"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  {faq.question}
                </h3>
                <p className="text-gray-600">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;

